import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Settings as SettingsIcon, Key, Save, ExternalLink, CheckCircle2, Trash2, Info, Bell, Moon, Sun } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { TurnstileWidget } from "../components/TurnstileWidget";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user, isAdmin, isDirector, setUser } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // MS365 config (stored in backend, not localStorage)
  const [ms365Config, setMs365Config] = useState({ client_id: "", tenant_id: "", client_secret: "" });
  const [ms365Saved, setMs365Saved] = useState(false);

  // Profile state
  const [profileName, setProfileName] = useState(user?.name || "");
  const [profileEmail, setProfileEmail] = useState(user?.email || "");
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [logoTurnstileToken, setLogoTurnstileToken] = useState("");
  const [logoTurnstileResetKey, setLogoTurnstileResetKey] = useState(0);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Notification settings
  const [notifications, setNotifications] = useState({
    email_reports_enabled: false,
    email_frequency: "weekly",
    email_recipients: [],
    slack_enabled: false,
    slack_webhook_url: "",
    teams_enabled: false,
    teams_webhook_url: "",
    low_utilization_alert: false,
    low_utilization_threshold: 50,
  });
  const [recipientInput, setRecipientInput] = useState("");
  const [savingNotifications, setSavingNotifications] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      // Load MS365 config from backend
      axios.get(`${API}/admin/ms365/config`).then(res => {
        if (res.data.configured) {
          setMs365Config({ client_id: res.data.client_id, tenant_id: res.data.tenant_id, client_secret: "" });
          setMs365Saved(true);
        }
      }).catch(() => {});

      // Load notification settings
      axios.get(`${API}/admin/notification-settings`).then(res => {
        if (res.data) {
          setNotifications(prev => ({ ...prev, ...res.data }));
          setRecipientInput((res.data.email_recipients || []).join(", "));
        }
      }).catch(() => {});
    }

    // Load user preferences
    axios.get(`${API}/user/preferences`).then(res => {
      if (res.data?.theme && res.data.theme !== theme) {
        // Sync theme from server if different
      }
    }).catch(() => {});
  }, [isAdmin]);

  const handleSaveMs365 = async () => {
    if (!ms365Config.client_id.trim() || !ms365Config.tenant_id.trim()) {
      toast.error("Client ID and Tenant ID required");
      return;
    }
    try {
      await axios.post(`${API}/admin/ms365/config`, ms365Config);
      setMs365Saved(true);
      toast.success("MS365 configuration saved");
    } catch { toast.error("Failed to save"); }
  };

  const handleClearMs365 = async () => {
    try {
      await axios.post(`${API}/admin/ms365/config`, { client_id: "", tenant_id: "", client_secret: "" });
      setMs365Config({ client_id: "", tenant_id: "", client_secret: "" });
      setMs365Saved(false);
      toast.success("MS365 configuration cleared");
    } catch { toast.error("Failed"); }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      const payload = {
        ...notifications,
        email_recipients: recipientInput.split(",").map(e => e.trim()).filter(Boolean),
      };
      await axios.post(`${API}/admin/notification-settings`, payload);
      toast.success("Notification settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSavingNotifications(false); }
  };

  const handleSavePreferences = async () => {
    try {
      await axios.put(`${API}/user/preferences`, { theme });
      toast.success("Preferences synced");
    } catch { toast.error("Failed"); }
  };

  const redirectUri = `${window.location.origin}/auth/callback`;

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">{isAdmin ? "Settings" : "Profile"}</h1>
        <p className="text-muted-foreground mt-1">{isAdmin ? "System configuration, integrations, and notifications" : "Manage your account"}</p>
      </div>

      {/* Company Logo - Admin only */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10"><SettingsIcon className="w-5 h-5 text-primary" /></div>
              <div><CardTitle className="text-base font-semibold">Company Logo</CardTitle><CardDescription>Upload your company logo (any size, PNG/JPG/SVG)</CardDescription></div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-xl border border-border bg-muted/30 flex items-center justify-center overflow-hidden">
                <img src={`/api/logo?t=${Date.now()}`} alt="SC" className="w-full h-full object-contain" onError={(e) => { e.target.onerror=null; e.target.src="data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 40 40%27%3E%3Crect width=%2740%27 height=%2740%27 rx=%278%27 fill=%27%233B82F6%27/%3E%3Ctext x=%2720%27 y=%2726%27 text-anchor=%27middle%27 fill=%27white%27 font-size=%2716%27 font-weight=%27bold%27%3ESC%3C/text%3E%3C/svg%3E"; }} />
              </div>
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*" id="logo-upload" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const formData = new FormData();
                      formData.append("file", file);
                      await axios.post(`${API}/admin/logo`, formData, { headers: { "Content-Type": "multipart/form-data", "X-Turnstile-Token": logoTurnstileToken || "" } });
                      toast.success("Logo uploaded! Refresh to see changes.");
                    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                  }} />
                  <Button variant="outline" size="sm" onClick={() => document.getElementById("logo-upload")?.click()}>Upload Logo</Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={async () => {
                    try { await axios.delete(`${API}/admin/logo`); toast.success("Logo removed"); } catch { toast.error("Failed"); }
                  }}>Remove</Button>
                </div>
                <p className="text-xs text-muted-foreground">Max 5MB. Displayed in login page and sidebar. Refresh page after uploading.</p>
                <TurnstileWidget onToken={setLogoTurnstileToken} onExpired={() => setLogoTurnstileToken("")} resetKey={logoTurnstileResetKey} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Appearance */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">{theme === "dark" ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-primary" />}</div>
            <div><CardTitle className="text-base font-semibold">Appearance</CardTitle><CardDescription>Theme and display preferences</CardDescription></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Dark Mode</p>
              <p className="text-sm text-muted-foreground">Switch between light and dark theme</p>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={theme === "dark"} onCheckedChange={toggleTheme} />
              <Button variant="outline" size="sm" onClick={handleSavePreferences}>Sync</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><SettingsIcon className="w-5 h-5 text-primary" /></div>
            <div><CardTitle className="text-base font-semibold">Profile</CardTitle><CardDescription>Update your details and password</CardDescription></div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Name</Label><Input value={profileName} onChange={(e) => setProfileName(e.target.value)} /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={profileEmail} onChange={(e) => setProfileEmail(e.target.value)} /></div>
          </div>
          <div className="space-y-2"><Label>Avatar URL</Label><Input value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} placeholder="https://..." /></div>
          <div className="flex justify-end">
            <Button variant="outline" size="sm" disabled={savingProfile} onClick={async () => {
              try {
                setSavingProfile(true);
                const res = await axios.put(`${API}/auth/me`, { name: profileName, email: profileEmail, avatar_url: avatarUrl || null });
                setUser(res.data); localStorage.setItem("auth_user", JSON.stringify(res.data));
                toast.success("Profile updated");
              } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              finally { setSavingProfile(false); }
            }}>{savingProfile ? "Saving..." : "Save Profile"}</Button>
          </div>
          <div className="pt-4 border-t border-border space-y-4">
            <p className="text-sm font-medium">Change Password</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Current Password</Label><Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} /></div>
              <div className="space-y-2"><Label>New Password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} /></div>
            </div>
            {newPassword && (
              <div className="p-3 rounded-lg bg-muted/50 space-y-1">
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Password Requirements:</p>
                <div className="grid grid-cols-2 gap-1">
                  {[
                    { check: newPassword.length >= 10, label: "Min 10 characters" },
                    { check: /[A-Z]/.test(newPassword), label: "Uppercase letter" },
                    { check: /[a-z]/.test(newPassword), label: "Lowercase letter" },
                    { check: /[0-9]/.test(newPassword), label: "Number" },
                    { check: /[!@#$%^&*()_+\-=\[\]{}|;':",./<>?]/.test(newPassword), label: "Special character" },
                  ].map((r, i) => (
                    <div key={i} className={`flex items-center gap-1.5 text-xs ${r.check ? "text-emerald-600" : "text-muted-foreground"}`}>
                      <span>{r.check ? "\u2713" : "\u2717"}</span>
                      <span>{r.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <Button variant="outline" size="sm" disabled={savingPassword || !currentPassword || !newPassword || newPassword.length < 10 || !/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword) || !/[!@#$%^&*()_+\-=\[\]{}|;':",./<>?]/.test(newPassword)} onClick={async () => {
                try { setSavingPassword(true); await axios.put(`${API}/auth/me/password`, { current_password: currentPassword, new_password: newPassword }); toast.success("Password updated"); setCurrentPassword(""); setNewPassword(""); }
                catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                finally { setSavingPassword(false); }
              }}>{savingPassword ? "Updating..." : "Change Password"}</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings - Admin only */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10"><Bell className="w-5 h-5 text-amber-500" /></div>
              <div><CardTitle className="text-base font-semibold">Notifications</CardTitle><CardDescription>Configure alerts and email reports</CardDescription></div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between">
              <div><p className="font-medium">Email Reports</p><p className="text-sm text-muted-foreground">Send periodic email reports</p></div>
              <Switch checked={notifications.email_reports_enabled} onCheckedChange={(c) => setNotifications(p => ({ ...p, email_reports_enabled: c }))} />
            </div>
            {notifications.email_reports_enabled && (
              <div className="space-y-3 pl-4 border-l-2 border-border">
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select value={notifications.email_frequency} onValueChange={(v) => setNotifications(p => ({ ...p, email_frequency: v }))}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Recipients (comma separated)</Label>
                  <Input value={recipientInput} onChange={(e) => setRecipientInput(e.target.value)} placeholder="director@company.com, admin@company.com" />
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <div><p className="font-medium">Low Utilization Alert</p><p className="text-sm text-muted-foreground">Alert when utilization drops below threshold</p></div>
              <Switch checked={notifications.low_utilization_alert} onCheckedChange={(c) => setNotifications(p => ({ ...p, low_utilization_alert: c }))} />
            </div>
            {notifications.low_utilization_alert && (
              <div className="pl-4 border-l-2 border-border">
                <div className="space-y-2">
                  <Label>Threshold (%)</Label>
                  <Input type="number" step="0.01" min="0" value={notifications.low_utilization_threshold} onChange={(e) => setNotifications(p => ({ ...p, low_utilization_threshold: parseFloat(e.target.value) || 50 }))} className="w-24" />
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <div><p className="font-medium">Slack Integration</p><p className="text-sm text-muted-foreground">Send alerts to Slack channel</p></div>
              <Switch checked={notifications.slack_enabled} onCheckedChange={(c) => setNotifications(p => ({ ...p, slack_enabled: c }))} />
            </div>
            {notifications.slack_enabled && (
              <div className="pl-4 border-l-2 border-border space-y-2">
                <Label>Slack Webhook URL</Label>
                <Input value={notifications.slack_webhook_url || ""} onChange={(e) => setNotifications(p => ({ ...p, slack_webhook_url: e.target.value }))} placeholder="https://hooks.slack.com/services/..." />
              </div>
            )}
            <div className="flex items-center justify-between">
              <div><p className="font-medium">Microsoft Teams</p><p className="text-sm text-muted-foreground">Send alerts to Teams channel</p></div>
              <Switch checked={notifications.teams_enabled} onCheckedChange={(c) => setNotifications(p => ({ ...p, teams_enabled: c }))} />
            </div>
            {notifications.teams_enabled && (
              <div className="pl-4 border-l-2 border-border space-y-2">
                <Label>Teams Webhook URL</Label>
                <Input value={notifications.teams_webhook_url || ""} onChange={(e) => setNotifications(p => ({ ...p, teams_webhook_url: e.target.value }))} placeholder="https://outlook.office.com/webhook/..." />
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={handleSaveNotifications} disabled={savingNotifications} className="gap-2">
                <Save className="w-4 h-4" /> {savingNotifications ? "Saving..." : "Save Notifications"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* MS365 Configuration - Admin only */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10"><Key className="w-5 h-5 text-blue-500" /></div>
              <div><CardTitle className="text-base font-semibold">Microsoft 365 Configuration</CardTitle><CardDescription>Azure AD credentials for SharePoint sync (self-hosted compatible)</CardDescription></div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
              <div className="flex items-start gap-3">
                <Info className="w-4 h-4 text-blue-500 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium mb-1">Setup Steps:</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-muted-foreground text-xs">
                    <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Azure Portal</a> → App registrations</li>
                    <li>Create new app, copy Client ID and Tenant ID</li>
                    <li>Create a client secret, copy the value</li>
                    <li>Add redirect URI: <code className="bg-muted px-1 rounded">{redirectUri}</code></li>
                    <li>API permissions: User.Read, Files.Read.All, Sites.Read.All</li>
                  </ol>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="space-y-2"><Label>Application (Client) ID</Label><Input value={ms365Config.client_id} onChange={(e) => { setMs365Config(p => ({ ...p, client_id: e.target.value })); setMs365Saved(false); }} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" /></div>
              <div className="space-y-2"><Label>Directory (Tenant) ID</Label><Input value={ms365Config.tenant_id} onChange={(e) => { setMs365Config(p => ({ ...p, tenant_id: e.target.value })); setMs365Saved(false); }} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" /></div>
              <div className="space-y-2"><Label>Client Secret</Label><Input type="password" value={ms365Config.client_secret} onChange={(e) => { setMs365Config(p => ({ ...p, client_secret: e.target.value })); setMs365Saved(false); }} placeholder="Enter secret (leave blank to keep existing)" /></div>
            </div>
            {ms365Saved && <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="w-4 h-4" /><span className="text-sm font-medium">Configuration saved</span></div>}
            <div className="flex items-center gap-3">
              <Button onClick={handleSaveMs365} className="gap-2"><Save className="w-4 h-4" /> Save</Button>
              {ms365Saved && <Button variant="outline" onClick={handleClearMs365} className="gap-2 text-red-600"><Trash2 className="w-4 h-4" /> Clear</Button>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Help */}
      {isAdmin && (
        <Card>
          <CardHeader><CardTitle className="text-base font-semibold">Help & Resources</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              <a href="https://learn.microsoft.com/en-us/graph/auth-register-app-v2" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-500 hover:underline"><ExternalLink className="w-4 h-4" />Microsoft Graph App Registration Guide</a>
              <a href="https://learn.microsoft.com/en-us/graph/api/resources/sharepoint" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-500 hover:underline"><ExternalLink className="w-4 h-4" />SharePoint API Documentation</a>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
